import { Component, OnInit } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject, throwError } from 'rxjs';
import { takeUntil, catchError, tap } from 'rxjs/operators';

import { IFilterConfig } from 'v2/modules/filter/interfaces';
import {
  DialogConfig,
  PaginationConfig,
  SnackBarConfig,
} from 'v2/modules/shared/configs';
import { IPagination } from 'v2/modules/shared/interfaces';
import { OrderTransferApiService } from 'v2/modules/shared/services/api/order';
import { trackByValue } from 'v2/modules/shared/util';
import { UiConfirmationDialogComponent } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

import { TransferOrderConfig } from '../../config';
import { IOrderTransferInterface } from '../../interfaces/order-transfers.interface';

interface IColumnConfig {
  id :string;
  label :string;
  visible :boolean;
  relation ?:string;
}

@Component({
  selector: 'lum-order-transfer',
  templateUrl: './order-transfer.component.html',
  styleUrls: ['./order-transfer.component.scss'],
})
export class OrderTransferComponent implements OnInit {
  filters :IFilterConfig[] = TransferOrderConfig.filterConfig;
  columns :IColumnConfig[] = TransferOrderConfig.columnConfig;
  relations = ['supplierPurchaseOrder.receivingReportRfq,supplierPurchaseOrder.modelAlternateIds'].join(',');

  orderTransfers$ :Observable<IOrderTransferInterface[]>; // TODO: add interface
  loading$ :Observable<boolean>;
  submitting$ :Observable<boolean>;
  pagination$ :Observable<IPagination>;
  paginationConfig = PaginationConfig;
  queryParams :{
    [param :string] :string | string[];
  } = {
    relations: this.relations,
    sortField: 'created_at',
    sortDirection: 'desc',
  };
  orderTransferSubscription :any | null = null; // TODO: Move or implement in service

  trackByValue = trackByValue;

  private onDestroy$ = new Subject();

  constructor(
    private dialog :MatDialog,
    private orderTransferApiService :OrderTransferApiService,
    private snackbar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initActions();
    this.getFromService();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    if (this.orderTransferSubscription) {
      // TODO: Move or implement in service
      this.orderTransferSubscription?.unsubscribe();
    }
  }

  onQueryBuild(event :any) :void {
    const { query } = event?.target?.value || event;
    this.queryParams = { ...this.queryParams, ...query };
    this.loadOrderTransfers(this.queryParams);
  }

  onPaginationEvent(event :PageEvent) :void {
    this.loadOrderTransfers({
      ...this.queryParams,
      limit: event.pageSize.toString(),
      page: (event.pageIndex + 1).toString(),
    });
  }

  onSelectColumn(event :MatCheckboxChange, filter :IColumnConfig) :void {
    const index = this.displayColumns.findIndex((row) => row.id === filter.id);
    this.displayColumns[index].visible = event.checked;
    this.getColumnRelations();
    this.loadOrderTransfers(this.queryParams);
  }

  onDeleteTransferOrder(order :IOrderTransferInterface) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Are you sure you want to delete this transfer order?',
          message: `This action will remove order #${order.orderNumber}.`,
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.orderTransferApiService
            .delete(order.id)
            .pipe(
              tap((response) => {
                this.loadOrderTransfers(this.queryParams);
                this.snackbar.open(response.success, 'Close', {
                  duration: 1500,
                });
              }),
              catchError((response) => {
                this.snackbar.open(response, 'Close', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                return throwError(() => response);
              }),
              takeUntil(this.onDestroy$),
            )
            .subscribe();
        }
      });
  }

  private initActions() :void {
    this.getColumnRelations();
    this.loadOrderTransfers(this.queryParams);
  }

  private loadOrderTransfers(params :{ [param :string] :string | string[] }) :void {
    if (this.orderTransferSubscription) {
      // TODO: Move or implement in service
      this.orderTransferSubscription?.unsubscribe();
    }

    this.orderTransferSubscription = this.orderTransferApiService.get(params);
  }

  private getFromService() :void {
    this.orderTransfers$ = this.orderTransferApiService.orderTransfers$;
    this.pagination$ = this.orderTransferApiService.pagination$;
    this.loading$ = this.orderTransferApiService.loading$;
    this.submitting$ = this.orderTransferApiService.submitting$;
  }

  private getColumnRelations() :void {
    this.queryParams = {
      ...this.queryParams,
      relations: this.relationFromFilters,
    };
  }

  get displayColumns() :IColumnConfig[] {
    return this.columns as IColumnConfig[];
  }

  get displayColumnCount() :number {
    return this.displayColumns.filter((row) => row.visible).length || 0;
  }

  get relationFromFilters() :string {
    return this.columns
      .filter(
        (filter :IColumnConfig) => filter.visible && filter.relation !== undefined,
      )
      .map((filter :IColumnConfig) => filter.relation)
      .concat(this.relations)
      .join(',') as string;
  }
}
